import * as fs from 'fs-extra';
import * as path from 'path';
import { VersionDetector, ValidationResult, ProjectMetadata } from './types';

export class DezzyVersionDetector implements VersionDetector {
  async detectVersion(projectPath: string): Promise<string | null> {
    try {
      // Check for explicit dezzy version in package.json
      const packageJsonPath = path.join(projectPath, 'package.json');
      if (await fs.pathExists(packageJsonPath)) {
        const packageJson = await fs.readJson(packageJsonPath);
        
        // Check for dezzy dependency
        const dezzyVersion = 
          packageJson.dependencies?.dezzy ||
          packageJson.devDependencies?.dezzy ||
          packageJson.peerDependencies?.dezzy;
          
        if (dezzyVersion) {
          return this.normalizeVersion(dezzyVersion);
        }
      }

      // Check for dezzy config file
      const configPath = path.join(projectPath, '.dezzy.json');
      if (await fs.pathExists(configPath)) {
        const config = await fs.readJson(configPath);
        if (config.version) {
          return config.version;
        }
      }

      // Detect version from project structure and files
      const structuralVersion = await this.detectFromStructure(projectPath);
      if (structuralVersion) {
        return structuralVersion;
      }

      return null;
    } catch (error) {
      console.warn(`Failed to detect version: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    }
  }

  async validateProject(projectPath: string): Promise<ValidationResult> {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    try {
      // Check if directory exists
      if (!(await fs.pathExists(projectPath))) {
        errors.push(`Project path does not exist: ${projectPath}`);
        return { valid: false, errors };
      }

      // Check for package.json
      const packageJsonPath = path.join(projectPath, 'package.json');
      if (!(await fs.pathExists(packageJsonPath))) {
        errors.push('No package.json found - this may not be a valid Dezzy project');
      } else {
        // Validate package.json structure
        const packageJson = await fs.readJson(packageJsonPath);
        if (!packageJson.name) {
          warnings.push('Package.json missing name field');
        }
        if (!packageJson.version) {
          warnings.push('Package.json missing version field');
        }
      }

      // Check for src directory
      const srcPath = path.join(projectPath, 'src');
      if (!(await fs.pathExists(srcPath))) {
        warnings.push('No src directory found');
      } else {
        // Check for common Dezzy directories
        const commonDirs = ['components', 'tokens', 'views'];
        for (const dir of commonDirs) {
          if (!(await fs.pathExists(path.join(srcPath, dir)))) {
            suggestions.push(`Consider adding ${dir} directory for better organization`);
          }
        }
      }

      // Check for TypeScript config
      const tsconfigPath = path.join(projectPath, 'tsconfig.json');
      if (!(await fs.pathExists(tsconfigPath))) {
        suggestions.push('Consider adding TypeScript support with tsconfig.json');
      }

      // Check for Storybook
      const storybookPath = path.join(projectPath, '.storybook');
      if (!(await fs.pathExists(storybookPath))) {
        suggestions.push('Consider adding Storybook for component documentation');
      }

      return {
        valid: errors.length === 0,
        errors: errors.length > 0 ? errors : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
        suggestions: suggestions.length > 0 ? suggestions : undefined
      };
    } catch (error) {
      errors.push(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { valid: false, errors };
    }
  }

  async extractMetadata(projectPath: string): Promise<ProjectMetadata> {
    const packageJsonPath = path.join(projectPath, 'package.json');
    const packageJson = await fs.readJson(packageJsonPath);
    
    const dezzyVersion = await this.detectVersion(projectPath) || '1.0.0';
    
    // Detect template from project structure
    const template = await this.detectTemplate(projectPath);
    
    // Extract features from dependencies and structure
    const features = await this.detectFeatures(projectPath, packageJson);
    
    // Check for migration history
    const migrationHistoryPath = path.join(projectPath, '.dezzy', 'migrations.json');
    let lastMigration;
    if (await fs.pathExists(migrationHistoryPath)) {
      const history = await fs.readJson(migrationHistoryPath);
      lastMigration = history.migrations?.[history.migrations.length - 1];
    }

    return {
      name: packageJson.name || path.basename(projectPath),
      version: packageJson.version || '1.0.0',
      dezzyVersion,
      template,
      features,
      dependencies: {
        ...packageJson.dependencies,
        ...packageJson.devDependencies
      },
      lastMigration
    };
  }

  private normalizeVersion(version: string): string {
    // Remove version prefixes and normalize
    return version.replace(/^[\^~]/, '').split('-')[0];
  }

  private async detectFromStructure(projectPath: string): Promise<string | null> {
    try {
      // Check for AI agent files (v2.0.0+)
      const agentFiles = [
        'src/agent.ts',
        'AGENT.md',
        'src/ui/AgentUI.tsx'
      ];
      
      for (const file of agentFiles) {
        if (await fs.pathExists(path.join(projectPath, file))) {
          return '2.0.0';
        }
      }

      // Check for enhanced section manifests (v1.1.0+)
      const srcPath = path.join(projectPath, 'src');
      if (await fs.pathExists(srcPath)) {
        const files = await fs.readdir(srcPath, { withFileTypes: true });
        
        for (const file of files) {
          if (file.isFile() && file.name.endsWith('.manifest.json')) {
            const manifestPath = path.join(srcPath, file.name);
            const manifest = await fs.readJson(manifestPath);
            
            // Check for v2 schema features
            if (manifest.category || manifest.accessibility || manifest.tokens) {
              return '1.1.0';
            }
          }
        }
      }

      // Check for basic Dezzy structure (v1.0.0+)
      const basicStructure = [
        'src/components',
        'src/tokens',
        '.storybook'
      ];
      
      let foundBasic = 0;
      for (const dir of basicStructure) {
        if (await fs.pathExists(path.join(projectPath, dir))) {
          foundBasic++;
        }
      }
      
      if (foundBasic >= 2) {
        return '1.0.0';
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  private async detectTemplate(projectPath: string): Promise<string | undefined> {
    try {
      const packageJsonPath = path.join(projectPath, 'package.json');
      const packageJson = await fs.readJson(packageJsonPath);

      // Check dependencies for template indicators
      const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };
      
      if (deps.next) return 'nextjs';
      if (deps.vite) return 'vite-react';
      if (deps['react-scripts']) return 'react-app';
      if (deps['@storybook/react'] && !deps.next && !deps.vite) return 'storybook-only';

      return undefined;
    } catch {
      return undefined;
    }
  }

  private async detectFeatures(projectPath: string, packageJson: any): Promise<string[]> {
    const features: string[] = [];
    const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };

    // Testing
    if (deps.jest || deps.vitest || deps['@testing-library/react']) {
      features.push('testing');
    }

    // Linting
    if (deps.eslint || deps.prettier) {
      features.push('linting');
    }

    // Storybook
    if (deps['@storybook/react']) {
      features.push('storybook');
    }

    // TypeScript
    if (deps.typescript || await fs.pathExists(path.join(projectPath, 'tsconfig.json'))) {
      features.push('typescript');
    }

    // AI Agent
    if (await fs.pathExists(path.join(projectPath, 'AGENT.md'))) {
      features.push('ai-agent');
    }

    // Dark mode
    const tokensPath = path.join(projectPath, 'src/tokens');
    if (await fs.pathExists(tokensPath)) {
      const files = await fs.readdir(tokensPath);
      if (files && files.some(f => f.includes('dark') || f.includes('theme'))) {
        features.push('dark-mode');
      }
    }

    return features;
  }

  // Utility method for CLI usage
  async detectMultipleProjects(rootPath: string): Promise<Array<{ path: string; version: string | null; valid: boolean }>> {
    const results: Array<{ path: string; version: string | null; valid: boolean }> = [];
    
    try {
      const entries = await fs.readdir(rootPath, { withFileTypes: true });
      
      for (const entry of entries) {
        if (entry.isDirectory()) {
          const projectPath = path.join(rootPath, entry.name);
          const validation = await this.validateProject(projectPath);
          
          if (validation.valid || validation.warnings?.length) {
            const version = await this.detectVersion(projectPath);
            results.push({
              path: projectPath,
              version,
              valid: validation.valid
            });
          }
        }
      }
    } catch (error) {
      // Silent fail for permission issues
    }
    
    return results;
  }

  // Check if version supports specific features
  supportsFeature(version: string, feature: string): boolean {
    const versionNum = this.versionToNumber(version);
    
    const featureVersions: Record<string, number> = {
      'ai-agent': this.versionToNumber('2.0.0'),
      'governance': this.versionToNumber('1.1.0'),
      'enhanced-manifests': this.versionToNumber('1.1.0'),
      'migration-system': this.versionToNumber('1.2.0')
    };
    
    return versionNum >= (featureVersions[feature] || 0);
  }

  private versionToNumber(version: string): number {
    const parts = version.split('.').map(Number);
    return (parts[0] || 0) * 10000 + (parts[1] || 0) * 100 + (parts[2] || 0);
  }
}